import React, { useEffect, useState } from "react";
import { useAdminAuth } from "../AdminAuthContext";
import {
  PageHeader, StatCard, Panel, Badge, Table,
} from "../components/UIKit";
import { AlertTriangle, Mic, HeartPulse, CheckCircle2, WifiOff } from "lucide-react";

function Ring({ value, label, color = "#14c2a1" }) {
  const r = 34;
  const c = 2 * Math.PI * r;
  const dash = (c * value) / 100;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative" style={{ width: 82, height: 82 }}>
        <svg width={82} height={82} className="-rotate-90">
          <circle cx={41} cy={41} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
          <circle
            cx={41}
            cy={41}
            r={r}
            stroke={color}
            strokeWidth="6"
            fill="none"
            strokeDasharray={`${dash} ${c}`}
            strokeLinecap="round"
            style={{ filter: `drop-shadow(0 0 6px ${color}66)` }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-white text-[18px] font-semibold">
          {value}%
        </div>
      </div>
      <div className="text-white/65 text-[11px] uppercase tracking-wider">{label}</div>
    </div>
  );
}

export default function AdminQuality() {
  const { api } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const fetch = () => api("/quality/stats").then(setStats).catch(() => {});
    fetch();
    const id = setInterval(fetch, 15000); // auto-refresh
    return () => clearInterval(id);
  }, [api, tick]);

  if (!stats) return <div className="p-8 text-white/60">Caricamento…</div>;

  return (
    <div className="p-8">
      <PageHeader
        title="Qualità Dati"
        subtitle="Monitoraggio real-time della qualità delle sessioni (refresh ogni 15s)"
      />

      <div className="grid grid-cols-4 gap-3">
        <StatCard
          label="Dispositivi attivi 24h"
          value={stats.active_devices_24h}
          sub={`su ${stats.total_users} totali`}
          icon={<CheckCircle2 size={14} />}
        />
        <StatCard
          label="Sessioni 24h"
          value={stats.sessions_24h}
          sub={`${stats.pct_complete}% complete`}
          accent="sky"
        />
        <StatCard
          label="Anomalie aperte"
          value={stats.anomalies.length}
          sub="ultime 30"
          accent="rose"
          icon={<AlertTriangle size={14} />}
        />
        <StatCard
          label="Dispositivi stale"
          value={stats.stale_users.length}
          sub="non trasmettono da > 48h"
          accent="amber"
          icon={<WifiOff size={14} />}
        />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-4">
        <Panel title="Copertura dati (24h)">
          <div className="flex items-center justify-around py-2">
            <Ring value={stats.pct_complete} label="Sessioni OK" color="#14c2a1" />
            <Ring value={stats.pct_hrv} label="HRV presente" color="#67e8d1" />
            <Ring value={stats.pct_mic} label="Mic granted" color="#f59e0b" />
          </div>
        </Panel>

        <Panel title="Dispositivi non trasmittenti">
          {stats.stale_users.length === 0 ? (
            <div className="text-white/50 text-sm">Nessun alert</div>
          ) : (
            <div className="space-y-2">
              {stats.stale_users.slice(0, 8).map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between rounded-lg border border-amber-400/20 bg-amber-400/5 px-3 py-2"
                >
                  <div>
                    <div className="text-white text-[13px]">
                      {u.first_name} {u.last_name}
                    </div>
                    <div className="text-white/55 text-[11px]">
                      {u.email}
                    </div>
                  </div>
                  <div className="text-amber-200 text-[11px] text-right">
                    <div>Ultima sessione</div>
                    <div className="text-white/80">
                      {new Date(u.last_session_at).toLocaleDateString("it-IT")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Anomalie recenti">
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {stats.anomalies.length === 0 && (
              <div className="text-white/50 text-sm">Nessuna anomalia</div>
            )}
            {stats.anomalies.slice(0, 14).map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between rounded-lg border border-rose-400/20 bg-rose-400/5 px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle size={12} className="text-rose-300 flex-shrink-0" />
                  <span className="text-white text-[12px]">
                    {a.anomalies?.join(", ")}
                  </span>
                </div>
                <span className="text-white/55 text-[11px]">
                  {new Date(a.started_at).toLocaleDateString("it-IT")}
                </span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
